import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ReactFlowProvider,
  Node,
  Edge,
  NodeChange,
  EdgeChange,
  applyNodeChanges,
  applyEdgeChanges,
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Panel,
  MarkerType,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import CustomNode from '@/components/CustomNode';

interface FullscreenVisualiseViewProps {
  initialNodes?: Node[];
  initialEdges?: Edge[];
  networkName?: string;
}

const FlowContent: React.FC<{
  nodes: Node[];
  edges: Edge[];
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  selectedCouriers: string[];
}> = ({ nodes, edges, onNodesChange, onEdgesChange, selectedCouriers }) => {
  const reactFlowInstance = useReactFlow();
  const [focusedEdgeId, setFocusedEdgeId] = useState<string | null>(null);

  // Define node types
  const nodeTypes = useMemo(
    () => ({
      customNode: CustomNode,
    }),
    []
  );

  // Define default edge options
  const defaultEdgeOptions = useMemo(
    () => ({
      type: 'default',
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 20,
        height: 20,
      },
      style: { strokeWidth: 2 },
      animated: false,
    }),
    []
  );

  // Handle edge focus
  const onEdgeClick = useCallback((_event: React.MouseEvent, edge: Edge) => {
    setFocusedEdgeId(edge.id);
  }, []);

  // Apply z-index to focused edges
  const styledEdges = useMemo(() => {
    return edges.map((edge) => ({
      ...edge,
      markerEnd: edge.markerEnd
        ? {
            ...(edge.markerEnd as object),
            ...(edge.id === focusedEdgeId ? { color: '#3b82f6' } : {}),
          }
        : undefined,
      markerStart: edge.markerStart
        ? {
            ...(edge.markerStart as object),
            type: (edge.markerStart as any).type || MarkerType.Arrow,
            ...(edge.id === focusedEdgeId ? { color: '#3b82f6' } : {}),
          }
        : undefined,
      ...(edge.id === focusedEdgeId ? { zIndex: 1000 } : {}),
      style: {
        ...edge.style,
        ...(edge.id === focusedEdgeId
          ? {
              stroke: '#3b82f6',
              strokeWidth: 3,
            }
          : {}),
      },
    }));
  }, [edges, focusedEdgeId]);

  // Fit view on mount
  useEffect(() => {
    setTimeout(() => {
      reactFlowInstance.fitView({ padding: 0.2 });
    }, 100);
  }, [reactFlowInstance]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={styledEdges as Edge[]}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onEdgeClick={onEdgeClick}
      nodeTypes={nodeTypes}
      defaultEdgeOptions={defaultEdgeOptions}
      fitView
      edgesFocusable={true}
      elementsSelectable={true}
      minZoom={0.1}
      maxZoom={4}
    >
      <Background />
      <Controls />
      <MiniMap />
      <Panel position="top-left" className="bg-white/90 backdrop-blur-sm px-4 py-3 rounded-lg shadow-lg">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-800">Network Visualization</h2>
          {selectedCouriers.length > 0 && (
            <span className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded-full">
              Filtered by {selectedCouriers.length} courier{selectedCouriers.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 mt-1">
          {nodes.length} locations · {edges.length} connections
        </p>
      </Panel>
      <Panel position="top-right">
        <button
          onClick={() => window.close()}
          className="px-4 py-2 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors shadow-lg"
        >
          Close
        </button>
      </Panel>
    </ReactFlow>
  );
};

const FullscreenVisualiseView: React.FC<FullscreenVisualiseViewProps> = ({
  initialNodes = [],
  initialEdges = [],
}) => {
  const [nodes, setNodes] = useState<Node[]>(initialNodes);
  const [edges, setEdges] = useState<Edge[]>(initialEdges);
  const [selectedCouriers, setSelectedCouriers] = useState<string[]>([]);

  // Update nodes and edges from parent when they change
  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes]);

  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges]);

  // Listen for messages from parent window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'UPDATE_GRAPH_DATA') {
        if (event.data.nodes) setNodes(event.data.nodes);
        if (event.data.edges) setEdges(event.data.edges);
        if (event.data.selectedCouriers !== undefined) {
          setSelectedCouriers(event.data.selectedCouriers);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Handle node changes
  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  // Handle edge changes
  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  // Filter edges based on selected couriers
  const filteredEdges = useMemo(() => {
    if (selectedCouriers.length === 0) return edges;
    
    return edges.filter((edge) => {
      const connection = edge.data?.connection as { courier?: string } | undefined;
      const edgeCourier = connection?.courier || edge.label;
      return edgeCourier && selectedCouriers.includes(edgeCourier as string);
    });
  }, [edges, selectedCouriers]);

  // Filter nodes based on filtered edges
  const filteredNodes = useMemo(() => {
    if (selectedCouriers.length === 0) return nodes;
    
    const connectedNodeIds = new Set<string>();
    filteredEdges.forEach((edge) => {
      connectedNodeIds.add(edge.source);
      connectedNodeIds.add(edge.target);
    });
    
    return nodes.filter((node) => connectedNodeIds.has(node.id));
  }, [nodes, filteredEdges, selectedCouriers]);

  return (
    <div className="w-screen h-screen">
      <ReactFlowProvider>
        <FlowContent
          nodes={filteredNodes}
          edges={filteredEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          selectedCouriers={selectedCouriers}
        />
      </ReactFlowProvider>
    </div>
  );
};

export default FullscreenVisualiseView;

